/**
 * Closed allowlist of facts a partner program rule may reference, with the operators and
 * value shape each one accepts. The API validates rules against this list with zod at save
 * time and again at evaluation time; the web rules editor builds its form from it. A rule
 * outside the allowlist is rejected, never evaluated.
 */
import { BUSINESS_ENTITY_TYPES, EQUIPMENT_CONDITIONS } from './api-types'

export const TRANSACTION_TYPES = ['equipment_purchase', 'other'] as const
export type TransactionType = (typeof TRANSACTION_TYPES)[number]

export const RULE_OPERATORS = ['eq', 'in', 'not_in', 'gte', 'lte', 'between'] as const
export type RuleOperator = (typeof RULE_OPERATORS)[number]

/** Operators over a single categorical value (state code, enum, category). */
export const CATEGORICAL_OPERATORS = ['eq', 'in', 'not_in'] as const satisfies readonly RuleOperator[]
/** Operators over an integer (minor units, months, model year). */
export const NUMERIC_OPERATORS = ['gte', 'lte', 'between'] as const satisfies readonly RuleOperator[]

export type RuleValueKind =
  | { kind: 'state' }
  | { kind: 'enum'; options: readonly string[] }
  | { kind: 'category' }
  | { kind: 'money_minor' }
  | { kind: 'integer'; min?: number; max?: number }

export interface RuleFieldSpec {
  label: string
  description: string
  operators: readonly RuleOperator[]
  value: RuleValueKind
  /** True when the fact is a set (one value per equipment line); every member must satisfy the rule. */
  multi: boolean
}

export const RULE_FIELD_SPECS = {
  'borrower.state': {
    label: 'Borrower state',
    description: 'Two-letter state of the buyer business.',
    operators: CATEGORICAL_OPERATORS,
    value: { kind: 'state' },
    multi: false,
  },
  'equipment.use_state': {
    label: 'Equipment use / delivery state',
    description: 'Two-letter state where the equipment will be delivered and used.',
    operators: CATEGORICAL_OPERATORS,
    value: { kind: 'state' },
    multi: false,
  },
  'equipment.condition': {
    label: 'Equipment condition',
    description: 'Condition of each equipment line; every line must satisfy the rule.',
    operators: CATEGORICAL_OPERATORS,
    value: { kind: 'enum', options: EQUIPMENT_CONDITIONS },
    multi: true,
  },
  'equipment.category': {
    label: 'Equipment category',
    description: 'Category of each equipment line; every line must satisfy the rule.',
    operators: CATEGORICAL_OPERATORS,
    value: { kind: 'category' },
    multi: true,
  },
  'equipment.model_year_min': {
    label: 'Oldest model year',
    description: 'The lowest model year across equipment lines; unknown if any line lacks one.',
    operators: NUMERIC_OPERATORS,
    value: { kind: 'integer', min: 1900, max: 2100 },
    multi: false,
  },
  requested_principal_minor: {
    label: 'Requested principal (minor units)',
    description: 'Amount the buyer asks to finance, in USD cents.',
    operators: NUMERIC_OPERATORS,
    value: { kind: 'money_minor' },
    multi: false,
  },
  invoice_total_minor: {
    label: 'Invoice total (minor units)',
    description: 'Sum of equipment lines and soft costs, in USD cents.',
    operators: NUMERIC_OPERATORS,
    value: { kind: 'money_minor' },
    multi: false,
  },
  time_in_business_months: {
    label: 'Time in business (months)',
    description: 'Collected by the borrower application; unknown until submitted.',
    operators: NUMERIC_OPERATORS,
    value: { kind: 'integer', min: 0, max: 2400 },
    multi: false,
  },
  entity_type: {
    label: 'Business entity type',
    description: 'Legal form of the buyer business.',
    operators: CATEGORICAL_OPERATORS,
    value: { kind: 'enum', options: BUSINESS_ENTITY_TYPES },
    multi: false,
  },
  transaction_type: {
    label: 'Transaction type',
    description: 'Kind of financing requested.',
    operators: CATEGORICAL_OPERATORS,
    value: { kind: 'enum', options: TRANSACTION_TYPES },
    multi: false,
  },
} as const satisfies Record<string, RuleFieldSpec>

export type RuleField = keyof typeof RULE_FIELD_SPECS
export const RULE_FIELDS = Object.keys(RULE_FIELD_SPECS) as readonly RuleField[]

export const EVIDENCE_LEVELS = ['stated', 'verified', 'partner_supplied'] as const
export type EvidenceLevel = (typeof EVIDENCE_LEVELS)[number]

/** A validated program rule as stored on `program_versions.rules` and echoed in API views. */
export type ProgramRuleView =
  | { field: RuleField; operator: 'eq'; value: string; min_evidence?: EvidenceLevel; deferred_to_partner?: boolean }
  | { field: RuleField; operator: 'in' | 'not_in'; value: string[]; min_evidence?: EvidenceLevel; deferred_to_partner?: boolean }
  | { field: RuleField; operator: 'gte' | 'lte'; value: number; min_evidence?: EvidenceLevel; deferred_to_partner?: boolean }
  | { field: RuleField; operator: 'between'; value: [number, number]; min_evidence?: EvidenceLevel; deferred_to_partner?: boolean }

export type EligibilityResult = 'eligible' | 'ineligible' | 'manual_review'

/** Reason codes the evaluator may emit. `rule_failed`/`fact_unknown`/`rule_invalid` carry the field. */
export type EligibilityReason =
  | 'jurisdiction_blocked'
  | 'jurisdiction_not_approved'
  | 'program_not_active'
  | 'program_expired'
  | 'criteria_unverified'
  | 'partner_not_active'
  | `rule_failed:${RuleField}`
  | `fact_unknown:${RuleField}`
  | `rule_invalid:${string}`

export type RuleEvaluationOutcome = 'pass' | 'fail' | 'unknown' | 'deferred_to_partner' | 'invalid'
